import { ReactElement, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { bottomNavigatorButtonInfo } from '../../../../constants/bottomNavigator/bottomNavigatorButtonInfo.constant';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { newProductConstant } from '../../../../constants/product/newProduct.constant';
import { useGetNewProduct } from '../../../../hooks/newProduct/useGetNewProduct';
import { INewProduct } from '../../../../interfaces/Product.interfaces';
import { isWithinNewProductPeriod } from '../../../../utils/calculator/calculateDate.util';
import { BottomNavigatorButton } from './BottomNavigatorButton';

interface BottomNavigatorProps {
  children?: ReactElement;
}

export const BottomNavigator = ({ children }: BottomNavigatorProps) => {
  const location = useLocation();
  const { newProduct, newOpenMarketProduct } = useGetNewProduct();

  const selectedNum = useMemo(() => {
    if (location.pathname === pageInfo.homePage.path) {
      return 0;
    }
    if (location.pathname === pageInfo.productListPage.path) {
      return 1;
    }
    if (location.pathname === pageInfo.openMarketListPage.path) {
      return 3;
    }
    if (location.pathname === pageInfo.infoListPage.path) {
      return 4;
    }

    return 5;
  }, [location.pathname]);

  const checkHasNewProductNotification = ({ newProduct, key }: { newProduct?: INewProduct; key: string }) => {
    if (!newProduct || !isWithinNewProductPeriod(newProduct.startOnSaleDate)) {
      return false;
    }

    const prevNotificationString = localStorage.getItem(key);
    const prevNotification = prevNotificationString ? JSON.parse(prevNotificationString) : null;

    const isSameNotification =
      prevNotification &&
      prevNotification.id === newProduct.id &&
      prevNotification.startOnSaleDate === newProduct.startOnSaleDate;

    if (isSameNotification) {
      return false;
    }

    return true;
  };

  // 마켓 알림 뱃지 여부
  const hasNewProductNotification = useMemo((): boolean => {
    if (location.pathname.includes(newProductConstant.market.path)) {
      return false;
    }

    return checkHasNewProductNotification({ newProduct, key: newProductConstant.market.key });
  }, [newProduct, location.pathname]);

  // 오픈마켓 알림 뱃지 여부
  const hasNewOpenMarketProductNotification = useMemo((): boolean => {
    if (location.pathname.includes(newProductConstant.openMarket.path)) {
      return false;
    }

    // 오픈마켓
    return checkHasNewProductNotification({ newProduct: newOpenMarketProduct, key: newProductConstant.openMarket.key });
  }, [newOpenMarketProduct, location.pathname]);

  const hasBottomNavigatorPathnameList = [
    pageInfo.infoListPage.path,
    pageInfo.infoDetailPage.path,
    pageInfo.homePage.path,
    pageInfo.productListPage.path,
    pageInfo.openMarketListPage.path,
    pageInfo.myInfoPage.path,
    pageInfo.loginPage.path,
  ];

  const isShow = useMemo<boolean>(() => {
    let hasBottomNavigator = false;

    // URL이 정확히 일치할 때
    hasBottomNavigatorPathnameList.forEach((pathname) => {
      if (location.pathname.includes(pathname)) {
        hasBottomNavigator = true;
      }
    });
    return hasBottomNavigator;
  }, [location.pathname]);

  return (
    <Container id={'bottom-navigation'} className={'backdrop-blur-sm'} isShow={isShow}>
      <TabList id={'tabs'}>
        {bottomNavigatorButtonInfo.map((navButton, index) => {
          let hasNotification = false;

          if (navButton.link === newProductConstant.market.path) {
            hasNotification = hasNewProductNotification;
          }

          if (navButton.link === newProductConstant.openMarket.path) {
            hasNotification = hasNewOpenMarketProductNotification;
          }

          const defaultBottomNavigatorButtonProps = {
            key: navButton.buttonTitle,
            link: navButton.link,
            isSelected: selectedNum === index,
            title: navButton.buttonTitle,
            defaultIcon: navButton.defaultIcon,
            pressedIcon: navButton.pressedIcon,
            isExternalLink: navButton.isExternalLink,
            hasNotification,
          };
          return <BottomNavigatorButton {...defaultBottomNavigatorButtonProps} />;
        })}
      </TabList>
      {children}
    </Container>
  );
};

const Container = styled.section<{ isShow: boolean }>`
  display: ${(props) => (props.isShow ? 'block' : 'none')};

  width: 100%;
  max-width: 512px;

  padding: 16px 24px 24px 24px;

  border-radius: 16px 16px 0px 0px;
  background: #ffffff;
  backdrop-filter: blur(8px);

  border: 1px solid var(--light-scheme-monochrome-grey-20, #e4e4ed);
  border-bottom: none;

  position: relative;
  z-index: 100;
`;

const TabList = styled.ul`
  width: 100%;

  display: flex;
  justify-content: space-between;
`;
